import React, { useRef, useState } from 'react'
import Button from '../atoms/Button'
import './FileUpload.css'

const FileUpload = ({ 
  onFileSelect, 
  accept = '', 
  multiple = false, 
  maxSize = null, 
  className = '',
  children,
  ...props 
}) => {
  const fileInputRef = useRef(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)

  const handleFileSelect = (files) => {
    if (isProcessing || !files || files.length === 0) return
    
    setIsProcessing(true)
    
    // Process files immediately without delay
    if (maxSize) {
      const validFiles = Array.from(files).filter(file => file.size <= maxSize)
      if (validFiles.length !== files.length) {
        alert(`Some files exceed the maximum size of ${maxSize / 1024 / 1024}MB`)
      }
      onFileSelect(multiple ? validFiles : validFiles[0] || null)
    } else {
      onFileSelect(multiple ? Array.from(files) : files[0] || null)
    }
    
    // Reset processing flag quickly
    setTimeout(() => setIsProcessing(false), 200)
  }

  const handleInputChange = (e) => {
    if (isProcessing) return // Prevent multiple calls
    
    const files = e.target.files
    handleFileSelect(files)
    
    // Reset the input value to allow selecting the same file again
    e.target.value = ''
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    handleFileSelect(e.dataTransfer.files)
  }

  const handleClick = (e) => {
    if (isProcessing) return // Prevent if already processing
    
    // Prevent the click from bubbling up if it's from the button
    if (e.target.tagName !== 'BUTTON' && !e.target.closest('button')) {
      fileInputRef.current?.click()
    }
  }

  const handleButtonClick = (e) => {
    if (isProcessing) return // Prevent if already processing
    
    e.stopPropagation() // Prevent the div's click handler
    fileInputRef.current?.click()
  }

  const classes = [
    'file-upload',
    isDragging && 'file-upload-dragging',
    className
  ].filter(Boolean).join(' ')

  return (
    <div 
      className={classes}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      {...props}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleInputChange}
        className="file-upload-input"
      />
      {children || (
        <div className="file-upload-default">
          <div className="file-upload-icon">📁</div>
          <p className="file-upload-text">
            Drop files here or click to browse
          </p>
          <Button variant="secondary" size="small" onClick={handleButtonClick}>
            Choose Files
          </Button>
        </div>
      )}
    </div>
  )
}

export default FileUpload
